import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  IconRefresh,
  IconCloudUpload,
  IconFileUpload,
  IconFileDescription,
  IconX,
  IconEye,
  IconReload,
  IconSparkles,
  IconLoader2,
} from '@tabler/icons-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig as ShadcnChartConfig,
} from '@/components/ui/chart';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { StatCard } from '../components/cards';

interface MtnInvoice {
  id: number;
  invoiceNumber: string;
  accountNumber: string | null;
  invoiceDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  billingYear: number | null;
  billingMonth: number | null;
  totalAmount: number | null;
  currency: string;
  fileName: string;
  uploadedBy: string | null;
  uploadedAt: string;
  extractionStatus: 'pending' | 'processing' | 'completed' | 'failed';
  extractionError: string | null;
  extractedAt: string | null;
  extractedData: any | null;
}

const statusVariantMap: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  completed: 'default',
  processing: 'secondary',
  failed: 'destructive',
  pending: 'outline',
};

export function MtnInvoicesPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState<MtnInvoice[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<MtnInvoice | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [generatingSummaryId, setGeneratingSummaryId] = useState<number | null>(null);
  const [invoiceSummary, setInvoiceSummary] = useState<{
    id: number;
    summaryText: string;
    generatedAt: string;
    previousPeriod: string;
    currentPeriod: string;
  } | null>(null);
  const [overwriteConfirm, setOverwriteConfirm] = useState<{
    open: boolean;
    changes: string[];
    message: string;
  } | null>(null);
  const [invoicesWithSummaries, setInvoicesWithSummaries] = useState<Set<number>>(new Set());

  const loadInvoices = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await api.get('/mtn/billing-recon/invoices');
      const invoicesData = res.data.data || [];
      setInvoices(invoicesData);
      setLastUpdatedAt(new Date());
      
      // Check which invoices have summaries
      const summaryChecks = await Promise.allSettled(
        invoicesData.map(async (invoice: MtnInvoice) => {
          if (invoice.extractionStatus === 'completed') {
            try {
              await api.get(`/mtn/billing-recon/invoices/${invoice.id}/summary`);
              return invoice.id;
            } catch {
              return null;
            }
          }
          return null;
        })
      );
      
      const hasSummaryIds = new Set<number>();
      summaryChecks.forEach((result) => {
        if (result.status === 'fulfilled' && result.value !== null) {
          hasSummaryIds.add(result.value);
        }
      });
      setInvoicesWithSummaries(hasSummaryIds);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoices');
      console.error('Error loading invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      setUploadFile(file);
      setError('');
    }
  };

  const handleUpload = async (overwrite: boolean = false) => {
    if (!uploadFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (overwrite) {
        formData.append('overwrite', 'true');
      }

      const params = overwrite ? { overwrite: 'true' } : {};
      const res = await api.post('/mtn/billing-recon/upload-invoice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params,
      });

      // Check if there's a comparison result
      if (res.data.comparison) {
        const comparison = res.data.comparison;
        if (comparison.hasChanges && !overwrite) {
          // Show confirmation dialog
          setOverwriteConfirm({
            open: true,
            changes: comparison.changes,
            message: comparison.message,
          });
          setUploading(false);
          return;
        } else {
          // Show comparison message
          if (comparison.message) {
            setError(comparison.message);
            setTimeout(() => setError(''), 5000);
          }
        }
      }

      setUploadFile(null);
      setError('');
      // Reset file input
      const fileInput = document.getElementById('invoice-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Reload invoices
      await loadInvoices();
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.requiresConfirmation) {
        // Show confirmation dialog for overwrite
        setOverwriteConfirm({
          open: true,
          changes: [],
          message: err.response.data.message,
        });
      } else {
        setError(err.response?.data?.message || 'Failed to upload invoice');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmOverwrite = () => {
    if (uploadFile) {
      setOverwriteConfirm(null);
      handleUpload(true);
    }
  };

  const handleViewDetails = async (invoice: MtnInvoice) => {
    setSelectedInvoice(invoice);
    setDetailDialogOpen(true);
    setInvoiceSummary(null);
    
    // Load summary if it exists
    if (invoice.extractionStatus === 'completed') {
      try {
        const res = await api.get(`/mtn/billing-recon/invoices/${invoice.id}/summary`);
        setInvoiceSummary({
          id: res.data.data.id,
          summaryText: res.data.data.summaryText,
          generatedAt: res.data.data.generatedAt,
          previousPeriod: `${res.data.data.previousBillingYear}-${String(res.data.data.previousBillingMonth).padStart(2, '0')}`,
          currentPeriod: `${res.data.data.currentBillingYear}-${String(res.data.data.currentBillingMonth).padStart(2, '0')}`,
        });
      } catch (err: any) {
        if (err.response?.status !== 404) {
          console.error('Error loading summary:', err);
        }
      }
    }
  };

  const handleGenerateSummary = async (invoiceId: number) => {
    setGeneratingSummaryId(invoiceId);
    setError('');

    try {
      const res = await api.post(`/mtn/billing-recon/invoices/${invoiceId}/generate-summary`);
      setError('');
      setInvoiceSummary({
        id: res.data.data.id,
        summaryText: res.data.data.summaryText,
        generatedAt: res.data.data.generatedAt,
        previousPeriod: `${res.data.data.previousBillingYear}-${String(res.data.data.previousBillingMonth).padStart(2, '0')}`,
        currentPeriod: `${res.data.data.currentBillingYear}-${String(res.data.data.currentBillingMonth).padStart(2, '0')}`,
      });
      setInvoicesWithSummaries(prev => new Set([...prev, invoiceId]));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate summary');
    } finally {
      setGeneratingSummaryId(null);
    }
  };

  const handleRetryExtraction = async (invoiceId: number) => {
    setRetryingId(invoiceId);
    setError('');

    try {
      const res = await api.post(`/mtn/billing-recon/invoices/${invoiceId}/retry`);
      setError('');
      await loadInvoices();
      
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice(res.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to retry extraction');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="MTN Invoices"
        subtitle="Upload and manage MTN billing invoices. AI extraction will process invoices automatically."
        action={
          <div className="flex items-center gap-4">
            {lastUpdatedAt && (
              <span className="text-sm text-muted-foreground">
                Last updated: {lastUpdatedAt.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              onClick={loadInvoices}
              disabled={loading}
            >
              {loading ? (
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <IconRefresh className="mr-2 h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>
        }
      />

      {/* Upload Invoice Section */}
      <MainCard className="mb-6 bg-primary/5">
        <h3 className="mb-1 flex items-center gap-2 text-lg font-semibold">
          <IconFileUpload className="h-5 w-5" />
          Upload MTN Invoice
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Upload a PDF invoice to extract data using AI. The invoice will be processed automatically.
        </p>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            disabled={uploading}
            nativeButton={false}
            render={<label htmlFor="invoice-upload" className="cursor-pointer inline-flex items-center" />}
          >
              <IconFileDescription className="mr-2 h-4 w-4" />
              {uploadFile ? uploadFile.name : 'Choose PDF File'}
              <input
                id="invoice-upload"
                type="file"
                hidden
                accept=".pdf"
                onChange={handleFileChange}
              />
          </Button>
          {uploadFile && (
            <Button
              onClick={() => handleUpload(false)}
              disabled={uploading}
            >
              {uploading ? (
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <IconCloudUpload className="mr-2 h-4 w-4" />
              )}
              {uploading ? 'Uploading...' : 'Upload & Extract'}
            </Button>
          )}
        </div>
      </MainCard>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => setError('')}>
              <IconX className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      {invoices.length > 0 && (
        <div className="mb-6 grid grid-cols-4 gap-6">
          <StatCard
            title="Total Invoices"
            value={invoices.length}
          />
          <StatCard
            title="Completed"
            value={invoices.filter(i => i.extractionStatus === 'completed').length}
          />
          <StatCard
            title="Processing"
            value={invoices.filter(i => i.extractionStatus === 'processing').length}
          />
          <StatCard
            title="Failed"
            value={invoices.filter(i => i.extractionStatus === 'failed').length}
          />
        </div>
      )}

      {/* Invoices Table */}
      <MainCard>
        <h3 className="mb-4 text-lg font-semibold">
          Uploaded Invoices
        </h3>

        {loading && invoices.length === 0 ? (
          <div className="flex justify-center p-8">
            <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : invoices.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead className="text-right">Total Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-center">AI Summary</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <span className="text-sm font-medium">
                      {invoice.invoiceNumber || '-'}
                    </span>
                    <div className="mt-1 flex items-center gap-2">
                      {invoice.billingYear && invoice.billingMonth && (
                        <span className="text-xs text-muted-foreground">
                          {invoice.billingYear}-{String(invoice.billingMonth).padStart(2, '0')}
                        </span>
                      )}
                      {invoice.billingYear && invoice.billingMonth && invoice.invoiceDate && (
                        <span className="text-xs text-muted-foreground">&bull;</span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDate(invoice.invoiceDate)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-sm">
                      {invoice.totalAmount
                        ? `R${Number(invoice.totalAmount).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariantMap[invoice.extractionStatus] || 'outline'}>
                      {invoice.extractionStatus}
                    </Badge>
                    {invoice.extractionError && (
                      <span className="mt-1 block text-xs text-destructive">
                        {invoice.extractionError.substring(0, 50)}...
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {invoice.uploadedBy || 'Unknown'}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {formatDateTime(invoice.uploadedAt)}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {invoice.extractionStatus === 'completed' && invoicesWithSummaries.has(invoice.id) && (
                      <IconSparkles className="mx-auto h-4 w-4 text-primary" title="AI Summary Available" />
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleViewDetails(invoice)}
                        title="View Details"
                      >
                        <IconEye className="h-4 w-4" />
                      </Button>
                      {(invoice.extractionStatus === 'processing' || invoice.extractionStatus === 'failed') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            'h-8 w-8',
                            invoice.extractionStatus === 'failed' && 'text-destructive hover:text-destructive'
                          )}
                          onClick={() => handleRetryExtraction(invoice.id)}
                          disabled={retryingId === invoice.id}
                          title="Retry Extraction"
                        >
                          {retryingId === invoice.id ? (
                            <IconLoader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <IconReload className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Alert>
            <AlertDescription>No invoices uploaded yet</AlertDescription>
          </Alert>
        )}
      </MainCard>

      {/* Invoice Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDetailDialogOpen(false);
            setInvoiceSummary(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Invoice Details: {selectedInvoice?.invoiceNumber}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Detailed view of invoice {selectedInvoice?.invoiceNumber}
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="rounded-md bg-muted/50 p-4">
                <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                  Basic Information
                </h4>
                <div className="space-y-1.5">
                  <p className="text-sm">
                    <strong>Invoice Number:</strong> {selectedInvoice.invoiceNumber}
                  </p>
                  <p className="text-sm">
                    <strong>Account Number:</strong> {selectedInvoice.accountNumber || '-'}
                  </p>
                  <p className="text-sm">
                    <strong>Invoice Date:</strong> {formatDate(selectedInvoice.invoiceDate)}
                  </p>
                  <p className="text-sm">
                    <strong>Period:</strong>{' '}
                    {selectedInvoice.periodStart && selectedInvoice.periodEnd
                      ? `${formatDate(selectedInvoice.periodStart)} to ${formatDate(selectedInvoice.periodEnd)}`
                      : '-'}
                  </p>
                  <p className="text-sm">
                    <strong>Total Amount:</strong>{' '}
                    {selectedInvoice.totalAmount
                      ? `R${Number(selectedInvoice.totalAmount).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '-'}
                  </p>
                  <p className="text-sm">
                    <strong>File Name:</strong> {selectedInvoice.fileName}
                  </p>
                  <p className="text-sm">
                    <strong>Uploaded By:</strong> {selectedInvoice.uploadedBy || '-'}
                  </p>
                  <p className="text-sm">
                    <strong>Uploaded At:</strong> {formatDateTime(selectedInvoice.uploadedAt)}
                  </p>
                  <p className="text-sm">
                    <strong>Status:</strong>{' '}
                    <Badge variant={statusVariantMap[selectedInvoice.extractionStatus] || 'outline'}>
                      {selectedInvoice.extractionStatus}
                    </Badge>
                  </p>
                </div>
              </div>

              {/* Invoice Comparison Summary */}
              {selectedInvoice.extractionStatus === 'completed' && (
                <div className="rounded-md bg-primary/5 p-4">
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="flex items-center gap-2 text-sm font-medium">
                      <IconSparkles className="h-4 w-4" />
                      Month-over-Month Comparison Summary
                    </h4>
                    {!invoiceSummary && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerateSummary(selectedInvoice.id)}
                        disabled={generatingSummaryId === selectedInvoice.id}
                      >
                        {generatingSummaryId === selectedInvoice.id ? (
                          <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <IconSparkles className="mr-2 h-4 w-4" />
                        )}
                        {generatingSummaryId === selectedInvoice.id ? 'Generating...' : 'Generate Summary'}
                      </Button>
                    )}
                  </div>
                  {invoiceSummary ? (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Comparing {invoiceSummary.previousPeriod} vs {invoiceSummary.currentPeriod}
                      </p>
                      <div className="prose prose-sm max-h-[500px] max-w-none overflow-auto rounded-md border bg-background p-4">
                        <ReactMarkdown>{invoiceSummary.summaryText}</ReactMarkdown>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Generated: {formatDateTime(invoiceSummary.generatedAt)}
                      </span>
                    </div>
                  ) : (
                    <Alert>
                      <AlertDescription>
                        No summary available yet. Click "Generate Summary" to create an AI-powered comparison with the previous month's invoice.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {/* Invoice Charts */}
              {selectedInvoice.extractedData && selectedInvoice.extractionStatus === 'completed' && (() => {
                const data = selectedInvoice.extractedData;
                const charges = data.charges || {};
                const hasCharges = charges.voice || charges.data || charges.sms || charges.other;
                
                const serviceData = [
                  { id: 'data', label: 'Data', value: charges.data || 0 },
                  { id: 'voice', label: 'Voice', value: charges.voice || 0 },
                  { id: 'sms', label: 'SMS', value: charges.sms || 0 },
                  { id: 'other', label: 'Other', value: charges.other || 0 },
                ].filter(item => item.value > 0);

                const summaryData = data.summary || {};
                const summaryItems = [
                  { label: 'Subtotal', value: summaryData.subtotal || 0 },
                  { label: 'Tax', value: summaryData.tax || 0 },
                  { label: 'Total', value: summaryData.total || data.totalAmount || 0 },
                ].filter(item => item.value > 0);

                return (
                  <div className="rounded-md border p-4">
                    <h4 className="mb-4 text-sm font-medium text-muted-foreground">
                      Invoice Analysis
                    </h4>
                    <div className="space-y-6">
                      {hasCharges && serviceData.length > 0 && (
                        <div>
                          <p className="mb-2 text-sm font-medium">
                            Service Breakdown
                          </p>
                          <ChartContainer
                            config={{
                              value: {
                                label: 'Amount (ZAR)',
                                color: 'hsl(221.2 83.2% 53.3%)',
                              },
                            }}
                            className="h-[300px] w-full"
                          >
                            <PieChart margin={{ top: 20, bottom: 20 }}>
                              <ChartTooltip
                                content={
                                  <ChartTooltipContent
                                    formatter={(value) => (
                                      <span className="font-mono tabular-nums">
                                        ZAR{' '}
                                        {typeof value === 'number'
                                          ? value.toLocaleString('en-ZA', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })
                                          : String(value)}
                                      </span>
                                    )}
                                  />
                                }
                              />
                              <Pie
                                data={serviceData}
                                dataKey="value"
                                nameKey="label"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label
                              >
                                {serviceData.map((_, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={`hsl(${(index * 360) / Math.max(serviceData.length, 1)} 65% 55%)`}
                                  />
                                ))}
                              </Pie>
                            </PieChart>
                          </ChartContainer>
                        </div>
                      )}
                      
                      {summaryItems.length > 0 && (
                        <div>
                          <p className="mb-2 text-sm font-medium">
                            Summary Breakdown
                          </p>
                          <ChartContainer
                            config={
                              {
                                amount: {
                                  label: 'Amount (ZAR)',
                                  color: 'hsl(221.2 83.2% 53.3%)',
                                },
                              } satisfies ShadcnChartConfig
                            }
                            className="h-[300px] w-full"
                          >
                            <BarChart
                              data={summaryItems}
                              margin={{ top: 20, right: 20, bottom: 60, left: 80 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                              <YAxis
                                tickFormatter={(v) =>
                                  `ZAR ${Number(v).toLocaleString('en-ZA', {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}`
                                }
                                label={{
                                  value: 'Amount (ZAR)',
                                  angle: -90,
                                  position: 'insideLeft',
                                }}
                              />
                              <ChartTooltip
                                content={
                                  <ChartTooltipContent
                                    formatter={(value) => (
                                      <span className="font-mono tabular-nums">
                                        ZAR{' '}
                                        {typeof value === 'number'
                                          ? value.toLocaleString('en-ZA', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })
                                          : String(value)}
                                      </span>
                                    )}
                                  />
                                }
                              />
                              <Bar dataKey="value" fill="var(--color-amount)" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ChartContainer>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {selectedInvoice.extractedData && (
                <div className="rounded-md border p-4">
                  <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                    Extracted Data
                  </h4>
                  <pre className="max-h-[400px] overflow-auto rounded-md bg-muted/50 p-4 text-sm">
                    {JSON.stringify(selectedInvoice.extractedData, null, 2)}
                  </pre>
                </div>
              )}

              {selectedInvoice.extractionError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <p className="mb-1 text-sm font-semibold">Extraction Error</p>
                    <p className="text-sm">{selectedInvoice.extractionError}</p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <DialogFooter>
            <div className="flex gap-2">
              {selectedInvoice && (selectedInvoice.extractionStatus === 'processing' || selectedInvoice.extractionStatus === 'failed') && (
                <Button
                  variant={selectedInvoice.extractionStatus === 'failed' ? 'destructive' : 'outline'}
                  onClick={() => handleRetryExtraction(selectedInvoice.id)}
                  disabled={retryingId === selectedInvoice.id}
                >
                  {retryingId === selectedInvoice.id ? (
                    <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <IconReload className="mr-2 h-4 w-4" />
                  )}
                  {retryingId === selectedInvoice.id ? 'Retrying...' : 'Retry Extraction'}
                </Button>
              )}
              <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overwrite Confirmation Dialog */}
      <Dialog
        open={overwriteConfirm?.open || false}
        onOpenChange={(open) => {
          if (!open) setOverwriteConfirm(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm Overwrite</DialogTitle>
            <DialogDescription className="sr-only">
              Confirm overwriting the existing invoice
            </DialogDescription>
          </DialogHeader>
          {overwriteConfirm && (
            <div className="space-y-4">
              <Alert variant="destructive" className="border-amber-500 bg-amber-50 text-amber-900 [&>svg]:text-amber-600">
                <AlertDescription>{overwriteConfirm.message}</AlertDescription>
              </Alert>
              {overwriteConfirm.changes.length > 0 && (
                <div className="rounded-md bg-muted/50 p-4">
                  <p className="mb-2 text-sm font-semibold">
                    Changes Detected:
                  </p>
                  <ul className="list-disc space-y-1 pl-5">
                    {overwriteConfirm.changes.map((change, idx) => (
                      <li key={idx} className="text-sm">
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Do you want to overwrite the existing invoice with the new data?
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverwriteConfirm(null)}>Cancel</Button>
            <Button
              variant="outline"
              onClick={handleConfirmOverwrite}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Yes, Overwrite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
